import { useState, useEffect, useCallback, useRef } from "react";
import { ClosedCaptioning, UploadSimple, DownloadSimple, FloppyDisk, Timer, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export default function SubtitleExtractor() {
  useSeo("Subtitle Extractor", "Upload video or audio and get editable subtitles with timestamps — export SRT, VTT or TXT in seconds.");
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [byokKey, setByokKey] = useState(() => localStorage.getItem("byok_openai_stt") || "");
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const loadMeta = useCallback(() => {
    api.get("/quota").then((r) => setQuota(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta, user]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const res = await api.get(`/jobs/${id}`);
      if (res.data.status !== "processing") {
        clearInterval(pollRef.current);
        setJob(res.data);
        loadMeta();
        if (res.data.status === "done") toast.success("Transcription complete");
        else toast.error(res.data.error || "Transcription failed");
      }
    }, 2500);
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Max file size is 25 MB");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (byokKey.trim()) form.append("apiKey", byokKey.trim());
      const res = await api.post("/transcribe", form);
      setJob(res.data);
      poll(res.data.id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const openJob = async (id) => {
    const res = await api.get(`/jobs/${id}`);
    setJob(res.data);
    if (res.data.status === "processing") poll(id);
  };

  const setSegText = (i, v) => setJob({ ...job, segments: job.segments.map((s, j) => (j === i ? { ...s, text: v } : s)) });

  const saveEdits = async () => {
    await api.put(`/jobs/${job.id}`, { segments: job.segments });
    toast.success("Edits saved");
  };

  const download = async (format) => {
    const res = await api.get(`/jobs/${job.id}/export`, { params: { format }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(job.filename || "transcript").split(".")[0]}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveKey = (v) => {
    setByokKey(v);
    localStorage.setItem("byok_openai_stt", v);
  };

  const minutesLeft = quota?.loggedIn ? Math.max(0, Math.round((quota.limitSeconds - quota.usedSeconds) / 60)) : null;

  return (
    <ToolShell slug="subtitle-extractor" title="Subtitle Extractor" tagline="Upload video or audio, get accurate subtitles with timestamps, edit inline, export SRT / VTT / TXT." hasResults={!!job}>
      {quota && (
        <div className="surface rounded-[14px] px-5 py-3.5 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="quota-banner">
          {quota.loggedIn ? (
            <p className="text-sm"><span className="font-bold text-[var(--success)]">{minutesLeft} free min</span> <span className="text-[var(--muted)]">left this month{byokKey ? " · your key = unlimited" : ""}</span></p>
          ) : (
            <p className="text-sm text-[var(--muted)]">{quota.freeUsed ? "Free try used —" : "1 free extraction, no login."} sign in for <span className="text-[var(--text)] font-semibold">30 free min/month</span></p>
          )}
          {!user && (
            <button data-testid="quota-signin-btn" onClick={startGoogleLogin} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--glow)] text-white text-xs font-semibold hover:bg-[#6a4ce6] transition-colors">
              <GoogleLogo size={14} weight="bold" /> Sign in with Google
            </button>
          )}
        </div>
      )}

      <div className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <input ref={inputRef} type="file" accept=".mp3,.mp4,.wav,.webm,.m4a,.mpeg,.mpga" onChange={onPick} className="hidden" data-testid="subtitle-file-input" />
        <button
          data-testid="subtitle-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || job?.status === "processing"}
          className="w-full border-2 border-dashed border-[var(--border-c)] hover:border-[var(--glow)] rounded-[14px] py-10 flex flex-col items-center gap-3 transition-colors disabled:opacity-60"
        >
          <UploadSimple size={28} color="var(--glow)" weight="duotone" />
          <span className="font-semibold text-sm">{uploading ? "Uploading..." : "Drop your video or audio here"}</span>
          <span className="text-xs text-[var(--muted)]">mp3 · mp4 · wav · webm · m4a — up to 25 MB</span>
        </button>
        <details className="mt-4">
          <summary className="text-xs text-[var(--muted)] cursor-pointer hover:text-[var(--text)]">Use your own OpenAI key (unlimited, stored in your browser only)</summary>
          <input
            data-testid="stt-byok-input"
            type="password"
            value={byokKey}
            onChange={(e) => saveKey(e.target.value)}
            placeholder="sk-..."
            className="mt-3 w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]"
          />
        </details>
      </div>

      {job?.status === "processing" && (
        <div className="mt-8 surface rounded-[20px] p-8 text-center fade-up" data-testid="transcribe-progress">
          <Timer size={28} color="var(--glow)" weight="duotone" className="mx-auto animate-pulse" />
          <p className="font-bold mt-3">Transcribing {job.filename}...</p>
          <p className="text-[var(--muted)] text-sm mt-1">Usually under a minute. Hang tight.</p>
        </div>
      )}

      {job?.status === "error" && (
        <div className="mt-8 surface rounded-[20px] p-6 fade-up border-[var(--coral)]" data-testid="transcribe-error">
          <p className="font-bold text-[var(--coral)]">Transcription failed</p>
          <p className="text-[var(--muted)] text-sm mt-1">{job.error}</p>
        </div>
      )}

      {job?.status === "done" && (
        <div className="mt-8 fade-up" data-testid="transcript-editor">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-bold text-lg">{job.filename}</h2>
              <p className="text-xs text-[var(--muted)]">{fmtTime(job.durationSeconds)} · {job.segments.length} segments</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="save-edits-btn" onClick={saveEdits} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full surface text-xs font-semibold hover:border-[var(--glow)] transition-colors">
                <FloppyDisk size={13} weight="bold" /> Save edits
              </button>
              {["srt", "vtt", "txt"].map((f) => (
                <button key={f} data-testid={`export-${f}-btn`} onClick={() => download(f)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--glow)] text-white text-xs font-semibold hover:bg-[#6a4ce6] transition-colors uppercase">
                  <DownloadSimple size={13} weight="bold" /> {f}
                </button>
              ))}
            </div>
          </div>
          <div className="surface rounded-[20px] p-4 sm:p-6 max-h-[480px] overflow-y-auto space-y-2">
            {job.segments.map((s, i) => (
              <div key={s.id ?? i} className="flex gap-3 items-start">
                <span className="shrink-0 text-[10px] font-bold text-[var(--glow)] bg-[var(--glow)]/10 rounded-full px-2.5 py-1 mt-1.5">{fmtTime(s.start)}</span>
                <textarea
                  data-testid={`segment-input-${i}`}
                  value={s.text}
                  onChange={(e) => setSegText(i, e.target.value)}
                  rows={1}
                  className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[10px] px-3 py-2 text-sm outline-none focus:border-[var(--glow)] resize-y"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="mt-10" data-testid="jobs-history">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><ClosedCaptioning size={18} color="var(--coral)" weight="duotone" /> Your transcripts</h2>
          <div className="space-y-2">
            {jobs.map((j) => (
              <button
                key={j.id}
                data-testid={`job-row-${j.id}`}
                onClick={() => openJob(j.id)}
                className="w-full surface rounded-[14px] px-4 py-3 flex items-center justify-between gap-3 text-left hover:border-[var(--glow)] transition-colors"
              >
                <span className="text-sm font-semibold truncate">{j.filename}</span>
                <span className="text-xs text-[var(--muted)] shrink-0">
                  {j.status === "done" ? fmtTime(j.durationSeconds) : j.status} · {dayjs(j.createdAt).format("MMM D")}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </ToolShell>
  );
}
